export function useChartDefaultValues() {
    const findMainPlot = (chart) => {
        if (!chart || !chart.panes) return null;

        let mainPlot = null;

        chart.panes.forEach(pane => {
            pane.axes.forEach(axe => {
                axe.plots.forEach(plot => {
                    if (plot.main === true) {
                        mainPlot = plot.curves[0];
                    }
                });
            });
        });

        return mainPlot;
    };

    const findMainAxe = (chart) => {
        if (!chart || !chart.panes) return null;

        let mainAxe;

        chart.panes.forEach(pane => {
            pane.axes.forEach(axe => {
                axe.plots.forEach(plot => {
                    if (plot.main === true) {
                        mainAxe = axe;
                    }
                });
            });
        });

        return mainAxe;
    };

    const getInitialValues = (defaultSettings, includeAggregation = true) => {
        const settings = [
            'line', 'scaleValue', 'scaleType', 'priceBox',
            'lineThickness', 'lineColors', 'preventLabelOverlap', 'gridLines', 'minorGridLines',
            'showNavigator', 'marginBars', 'crosshair', 'trackball', 'labels',
            'snapAnnotationsToPrices', 'comparisons', 'density', 'tooltip', 'vertical'
        ];

        if (includeAggregation) settings.push('aggregation');

        return settings.reduce((values, setting) => {
            values[setting] = getDefaultValue(setting, defaultSettings);
            return values;
        }, {});

        // return settings.reduce((values, setting) => {
        //     const func = 'getDefault' + setting[0].toUpperCase() + setting.slice(1);
        //     values[setting] = this[func](defaultSettings);
        //     return values;
        // }, {});
    };

    const getDefaultValue = (setting, chart) => {
        switch (setting) {
            case 'line':
                return getDefaultLine(chart);
            case 'scaleValue':
                return getDefaultScaleValue(chart);
            case 'scaleType':
                return getDefaultScaleType(chart);
            case 'priceBox':
                return getDefaultPriceBox(chart);
            case 'lineThickness':
                return getDefaultLineThickness(chart);
            case 'lineColors':
                return getDefaultLineColors(chart);
            case 'preventLabelOverlap':
                return getDefaultPreventLabelOverlap(chart);
            case 'gridLines':
                return getDefaultGridLines(chart);
            case 'minorGridLines':
                return getDefaultMinorGridLines(chart);
            case 'showNavigator':
                return getDefaultShowNavigator(chart);
            case 'marginBars':
                return getDefaultMarginBars(chart);
            case 'crosshair':
                return getDefaultCrosshair(chart);
            case 'trackball':
                return getDefaultTrackball(chart);
            case 'labels':
                return getDefaultLabels(chart);
            case 'snapAnnotationsToPrices':
                return getDefaultSnapAnnotationsToPrices(chart);
            case 'comparisons':
                return getDefaultComparisons(chart);
            case 'density':
                return getDefaultDensity(chart);
            case 'tooltip':
                return getDefaultTooltip(chart);
            case 'vertical':
                return getDefaultVertical(chart);
            case 'aggregation':
                return getDefaultAggregation(chart);
            case 'range':
                return getDefaultRange(chart);
            case 'period':
                return getDefaultPeriod(chart);
            default:
                console.warn(`No default function for setting: ${setting}`);
                return null;
        }
    };

    function getDefaultLine(chart) {
        const mainPlot = findMainPlot(chart);
        const attributes = mainPlot && Array.isArray(mainPlot.attributes) ? mainPlot.attributes[0] : false;

        if (mainPlot && mainPlot.style === 'Candlestick' && attributes === 'ChangeBased') {
            return 'CandlestickCloseToClose';
        } else if (mainPlot && mainPlot.style === 'Candlestick') {
            return 'CandlestickOpenToClose';
        } else if (mainPlot && mainPlot.style === 'OHLC' && attributes === 'ChangeBased') {
            return 'ColoredOHLC';
        }

        if (mainPlot) {
            return mainPlot.style;
        } else {
            return 'OHLC';
        }
    };

    function getDefaultScaleValue(chart) {
        const mainAxe = findMainAxe(chart);

        if (mainAxe) {
            return mainAxe.comparison === 'Percent' ? 'percentChange' : 'priceChange';
        } else {
            return 'priceChange';
        }
    };

    function getDefaultScaleType(chart) {
        const mainAxe = findMainAxe(chart);

        if (mainAxe) {
            return mainAxe.scale;
        } else {
            return 'Linear';
        }
    };

    function getDefaultDensity(chart) {
        if (chart && chart.display) {
            return chart.display.density || 75;
        } else {
            return 75;
        }
    };

    function getDefaultTooltip(chart) {
        if (chart && chart.display) {
            if (!chart.display.tooltip.visible) {
                return 'hide';
            }

            return chart.display.tooltip.mode;
        } else {
            return 'cards';
        }
    };

    function getDefaultPriceBox(chart) {
        if (chart && chart.display) {
            return chart.display.tooltip.mode === 'standard' ? true : false;
        } else {
            return false;
        }
    };

    function getDefaultLineThickness(chart) {
        const mainPlot = findMainPlot(chart);

        if (mainPlot) {
            return mainPlot.lineWidth;
        } else {
            return 1;
        }
    };

    function getDefaultLineColors(chart) {
        const mainPlot = findMainPlot(chart);

        if (mainPlot) {
            return mainPlot.colors;
        } else {
            return ['#000000'];
        }
    };

    function getDefaultPreventLabelOverlap(chart) {
        return false;
    };

    function getDefaultCrosshair(chart) {
        if (chart && chart.display) {
            return chart.display.yAxis.crosshair.enabled;
        } else {
            return true;
        }
    };

    function getDefaultVertical(chart) {
        if (chart && chart.display) {
            return !chart.display.yAxis.crosshair.enabled && chart.display.xAxis.crosshair.enabled;
        } else {
            return true;
        }
    };

    function getDefaultTrackball(chart) {
        if (chart && chart.display) {
            return chart.display.chart.plotHover;
        } else {
            return false;
        }
    };

    function getDefaultGridLines(chart) {
        if (chart && chart.display) {
            return chart.display.yAxis.gridLines.visible;
        } else {
            return true;
        }
    };

    function getDefaultMinorGridLines(chart) {
        if (chart && chart.display) {
            return chart.display.yAxis.minorGridLines.visible;
        } else {
            return false;
        }
    };

    function getDefaultLabels(chart) {
        if (chart && chart.display) {
            if (chart.display.yAxis.showLastValue === 'Main') {
                return 'main';
            } else {
                return chart.display.yAxis.preventLabelOverlap ? 'separate' : 'overlap';
            }
        } else {
            return 'overlap';
        }
    };

    function getDefaultShowNavigator(chart) {
        return true;
    };

    function getDefaultMarginBars(chart) {
        if (chart && chart.display) {
            return chart.display.xAxis.marginBars;
        } else {
            return 2;
        }
    };

    function getDefaultAggregation(chart) {
        if (chart && chart.data) {
            return chart.data.aggregation;
        } else {
            return {
                unit: 'Day',
                size: 1,
                spec: 'None'
            };
        }
    };

    function getDefaultRange(chart) {
        if (chart && chart.display) {
            return chart.display.xAxis.visibleRange;
        } else {
            return null;
        }
    };

    function getDefaultPeriod(chart) {
        if (chart && chart.display) {
            return chart.display.period;
        } else {
            return null;
        }
    };

    function getDefaultSnapAnnotationsToPrices(chart) {
        if (chart && chart.display) {
            return chart.display.yAxis.snapAnnotationsToPrices;
        } else {
            return false;
        }
    };

    function getDefaultComparisons(chart) {
        if (chart && chart.panes) {
            const comparison = {
                compares: [],
                percentChangeScale: getDefaultScaleValue(chart) === 'percentChange'
            };

            chart.panes.map(pane => {
                pane.axes.map(axis => {
                    const mainAxe = findMainAxe(chart);

                    axis.plots.map(plot => {
                        if (plot.type === 'Symbol' && plot.symbol !== 'PLACEHOLDER') {
                            const isRightScale = mainAxe.plots.includes(plot);

                            comparison.compares.push({
                                symbol: plot.symbol,
                                leftScale: !isRightScale,
                                color: plot.curves[0].colors[0]
                            });
                        }
                    });
                });
            });

            return comparison;
        }
    };

    return {
        findMainPlot,
        findMainAxe,
        getInitialValues,
    };
}
